import React from "react";

/**
 * Skeleton placeholders.
 *
 * Pure presentation: these render greyed shapes with the same box sizes as
 * the content they stand in for, so swapping them out causes no layout shift.
 * They never fetch, never hold state and take no callbacks.
 */

export function Skeleton({ variant = "text", width, height, className = "", style }) {
  const variantClass = variant ? " pp-skeleton--" + variant : "";
  return (
    <span
      className={"pp-skeleton" + variantClass + (className ? " " + className : "")}
      style={{ width, height, ...style }}
      aria-hidden="true"
    />
  );
}

/** A stack of text lines, last one short — reads as a paragraph. */
export function SkeletonText({ lines = 3, className = "" }) {
  const widths = ["100%", "92%", "78%", "85%", "66%"];
  return (
    <span
      className={"pp-skeleton-stack" + (className ? " " + className : "")}
      aria-hidden="true"
    >
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} variant="text" width={widths[i % widths.length]} />
      ))}
    </span>
  );
}

/** Avatar + two lines, matching the profile identity block. */
export function SkeletonIdentity({ className = "" }) {
  return (
    <div
      className={"d-flex align-items-center" + (className ? " " + className : "")}
      style={{ gap: 12 }}
      aria-hidden="true"
    >
      <Skeleton variant="avatar" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="52%" height={13} />
        <Skeleton variant="text" width="72%" />
      </div>
    </div>
  );
}

/** Label/value pairs, matching the two-column detail lists. */
export function SkeletonFieldList({ rows = 6, className = "" }) {
  return (
    <div className={className} aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div className="row pt-2" key={i}>
          <div className="col-md-6">
            <Skeleton variant="text" width={i % 2 ? "62%" : "78%"} />
          </div>
          <div className="col-md-6">
            <Skeleton variant="text" width={i % 3 ? "54%" : "40%"} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Rows for list/table style regions. */
export function SkeletonRows({ rows = 5, columns = 4, className = "" }) {
  return (
    <div className={className} aria-hidden="true">
      {Array.from({ length: rows }).map((_, r) => (
        <div className="pp-skeleton-row" key={r}>
          {Array.from({ length: columns }).map((__, c) => (
            <Skeleton
              key={c}
              variant="text"
              width={c === 0 ? "26%" : "18%"}
              style={{ flex: c === 0 ? "0 0 26%" : "1 1 auto" }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export default Skeleton;
